/**
 * Firmware Upload Component
 * Handles OTA firmware updates via BLE
 */

import { useState, useRef, useEffect } from 'react'
import { Card } from '../Common/Card'
import { Button } from '../Common/Button'
import { useMScaleStore } from '../../store'
import { bluetoothService } from '../../services/bluetooth/BluetoothService'
import { OTAState } from '../../services/bluetooth/OTAService'
import { FirmwareService } from '../../services/firmware/FirmwareService'
import { getLatestFirmware, isNewerVersion, type FirmwareMetadata } from '../../services/firmware/FirmwareManifest'

export function FirmwareUpload() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedFirmware, setSelectedFirmware] = useState<FirmwareMetadata | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStatus, setUploadStatus] = useState<string>('')
  const [otaState, setOTAState] = useState<OTAState>(OTAState.IDLE)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [latestFirmware, setLatestFirmware] = useState<FirmwareMetadata | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { otaStatus, firmwareVersion } = useMScaleStore()

  // Sync OTA state from store
  useEffect(() => {
    if (otaStatus) {
      setOTAState(otaStatus.state)
      console.log('OTA state updated from device:', otaStatus)
    }
  }, [otaStatus])

  // Load latest firmware metadata on mount
  useEffect(() => {
    getLatestFirmware().then(firmware => {
      setLatestFirmware(firmware)
    }).catch(error => {
      console.error('Failed to load latest firmware:', error)
    })
  }, [])

  const isUpdateAvailable = latestFirmware && firmwareVersion
    ? isNewerVersion(firmwareVersion, latestFirmware.version)
    : false

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      const file = files[0]

      // Validate file
      if (!file.name.endsWith('.bin')) {
        alert('Please select a .bin firmware file')
        return
      }

      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('Firmware file is too large (max 2MB)')
        return
      }

      if (file.size < 100 * 1024) {
        alert('Firmware file seems too small (min 100KB)')
        return
      }

      setSelectedFile(file)
      setUploadStatus(`Selected: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`)
    }
  }

  const handleUpdateToLatest = async () => {
    if (!latestFirmware) {
      alert('No firmware available')
      return
    }

    setSelectedFirmware(latestFirmware)

    const confirmMessage = `
Update to the latest firmware?

Current Version: ${firmwareVersion || 'Unknown'}
New Version: ${latestFirmware.version}
Size: ${FirmwareService.formatSize(latestFirmware.size)}

The device will restart after the update.
Make sure the device is not in use during the update.

Changes in this version:
${latestFirmware.changelog.map(c => `• ${c}`).join('\n')}
    `.trim()

    if (!confirm(confirmMessage)) {
      return
    }

    try {
      setIsUploading(true)
      setUploadProgress(0)
      setUploadStatus('Loading firmware...')
      setOTAState(OTAState.STARTING)

      // Load firmware file from bundled assets
      const file = await FirmwareService.loadFirmware(latestFirmware)
      setSelectedFile(file)
      setUploadStatus(`Loaded ${file.name}`)

      // Validate firmware
      const validation = FirmwareService.validateFirmware(file, latestFirmware)
      if (!validation.valid) {
        throw new Error(`Firmware validation failed: ${validation.errors.join(', ')}`)
      }

      // Upload firmware
      await uploadFirmware(file)

    } catch (error) {
      console.error('Update to latest failed:', error)
      setUploadStatus(`Update failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
      setOTAState(OTAState.ERROR)
      setIsUploading(false)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a firmware file first')
      return
    }

    // Validate manually selected file
    const validation = FirmwareService.validateFirmware(selectedFile)
    if (!validation.valid) {
      alert(`Invalid firmware file:\n\n${validation.errors.join('\n')}`)
      return
    }

    if (!confirm(`
Are you sure you want to update the firmware?

File: ${selectedFile.name}
Size: ${FirmwareService.formatSize(selectedFile.size)}

The device will restart after the update.
Make sure the device is not in use during the update.
    `.trim())) {
      return
    }

    await uploadFirmware(selectedFile)
  }

  const uploadFirmware = async (file: File) => {

    setIsUploading(true)
    setUploadProgress(0)
    setUploadStatus('Starting OTA update...')
    setOTAState(OTAState.STARTING)

    try {
      await bluetoothService.uploadFirmware(
        file,
        (percent, bytes, total) => {
          setUploadProgress(percent)
          setUploadStatus(`Uploading... ${percent}% (${(bytes / 1024).toFixed(1)} / ${(total / 1024).toFixed(1)} KB)`)
        }
      )

      setUploadStatus('Firmware uploaded successfully! Device will restart...')
      setOTAState(OTAState.COMPLETE)

      // Clear selections
      setSelectedFile(null)
      setSelectedFirmware(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      // Show success message for a few seconds
      setTimeout(() => {
        setIsUploading(false)
        setUploadProgress(0)
        setUploadStatus('OTA update complete. Device should restart automatically.')
      }, 3000)

    } catch (error) {
      console.error('OTA upload failed:', error)
      setUploadStatus(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
      setOTAState(OTAState.ERROR)
      setIsUploading(false)
    }
  }

  const handleCancel = () => {
    if (confirm('Are you sure you want to cancel the upload?')) {
      bluetoothService.abortOTA()
      setIsUploading(false)
      setUploadProgress(0)
      setUploadStatus('Upload canceled')
      setOTAState(OTAState.IDLE)
    }
  }

  const getStateColor = (state: OTAState) => {
    switch (state) {
      case OTAState.IDLE: return 'text-gray-500 dark:text-gray-400'
      case OTAState.STARTING: return 'text-blue-600 dark:text-blue-400'
      case OTAState.RECEIVING: return 'text-blue-600 dark:text-blue-400'
      case OTAState.VALIDATING: return 'text-yellow-600 dark:text-yellow-400'
      case OTAState.COMPLETE: return 'text-green-600 dark:text-green-400'
      case OTAState.ERROR: return 'text-red-600 dark:text-red-400'
      default: return 'text-gray-500 dark:text-gray-400'
    }
  }

  const getStateName = (state: OTAState) => {
    switch (state) {
      case OTAState.IDLE: return 'Idle'
      case OTAState.STARTING: return 'Starting'
      case OTAState.RECEIVING: return 'Receiving'
      case OTAState.VALIDATING: return 'Validating'
      case OTAState.COMPLETE: return 'Complete'
      case OTAState.ERROR: return 'Error'
      default: return 'Unknown'
    }
  }

  return (
    <Card title="Firmware Update (OTA)" className="mb-4 sm:mb-6">
      <div className="space-y-4 sm:space-y-6">
        {/* Current Version & Update Status */}
        <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div>
            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Current Firmware
            </div>
            <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              {firmwareVersion ? FirmwareService.formatVersion(firmwareVersion) : 'Unknown'}
            </div>
          </div>
          {isUpdateAvailable && (
            <div className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs sm:text-sm font-medium">
              Update Available
            </div>
          )}
        </div>

        {/* Latest Firmware Info - Only show if update available */}
        {latestFirmware && isUpdateAvailable && (
          <div className="p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-sm sm:text-base font-semibold text-blue-900 dark:text-blue-200">
                  {FirmwareService.formatVersion(latestFirmware.version)} - Latest Release
                </h3>
                <div className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  {new Date(latestFirmware.buildDate).toLocaleDateString()} • {FirmwareService.formatSize(latestFirmware.size)}
                </div>
              </div>
              {latestFirmware.critical && (
                <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-medium">
                  Critical
                </span>
              )}
            </div>
            <div className="text-xs sm:text-sm text-blue-800 dark:text-blue-300 mt-3">
              <div className="font-medium mb-1">What's New:</div>
              <ul className="space-y-1 list-disc list-inside">
                {latestFirmware.changelog.map((change, idx) => (
                  <li key={idx}>{change}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Up to Date Status - Show when firmware is current */}
        {latestFirmware && !isUpdateAvailable && !isUploading && firmwareVersion && (
          <div className="p-4 sm:p-5 rounded-lg border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <div className="text-sm sm:text-base font-semibold text-green-700 dark:text-green-300">
                You're running the latest firmware
              </div>
            </div>
            <div className="text-xs sm:text-sm text-green-600 dark:text-green-400 mb-3">
              Version {FirmwareService.formatVersion(firmwareVersion)} • Released {new Date(latestFirmware.buildDate).toLocaleDateString()}
            </div>
            <div className="text-xs text-green-600 dark:text-green-400">
              <div className="font-medium mb-1">Latest features:</div>
              <ul className="space-y-1 list-disc list-inside">
                {latestFirmware.changelog.map((change, idx) => (
                  <li key={idx}>{change}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Update to Latest Button */}
        {latestFirmware && isUpdateAvailable && (
          <Button
            variant="primary"
            onClick={handleUpdateToLatest}
            disabled={isUploading}
            className="w-full text-base sm:text-lg py-3"
          >
            Update to {FirmwareService.formatVersion(latestFirmware.version)}
          </Button>
        )}

        {/* Consolidated OTA Progress & Status */}
        {(isUploading || (otaStatus && otaStatus.state !== OTAState.IDLE)) && (
          <div className={`p-4 sm:p-5 rounded-lg border ${
            otaStatus?.state === OTAState.ERROR
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              : otaStatus?.state === OTAState.COMPLETE
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
          }`}>
            {/* Header with State and Spinner */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`text-sm sm:text-base font-semibold ${getStateColor(otaState)}`}>
                  {getStateName(otaState)}
                </div>
                {isUploading && (otaState === OTAState.RECEIVING || otaState === OTAState.STARTING || otaState === OTAState.VALIDATING) && (
                  <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                )}
              </div>
              <span className="text-base sm:text-lg font-bold text-blue-600 dark:text-blue-400">
                {otaStatus?.state === OTAState.RECEIVING || otaStatus?.state === OTAState.VALIDATING
                  ? `${otaStatus.progress}%`
                  : `${uploadProgress}%`}
              </span>
            </div>

            {/* Progress Bar */}
            {isUploading && otaStatus?.state !== OTAState.ERROR && (
              <div className="mb-3">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 sm:h-4">
                  <div
                    className="bg-blue-600 dark:bg-blue-500 h-3 sm:h-4 rounded-full transition-all duration-300"
                    style={{
                      width: `${otaStatus?.state === OTAState.RECEIVING || otaStatus?.state === OTAState.VALIDATING
                        ? otaStatus.progress
                        : uploadProgress}%`
                    }}
                  />
                </div>
              </div>
            )}

            {/* Status Message */}
            <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mb-2">
              {uploadStatus}
            </div>

            {/* Device Error Details */}
            {otaStatus?.state === OTAState.ERROR && (
              <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-800">
                <div className="text-sm text-red-800 dark:text-red-300">
                  Error Code: {otaStatus.error}
                </div>
                {otaStatus.error === 2 && (
                  <div className="text-xs text-red-700 dark:text-red-400 mt-1">
                    Write failed - firmware may be incompatible with device
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Advanced Options */}
        <div>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium"
          >
            {showAdvanced ? '▼' : '▶'} Advanced: Upload Custom Firmware
          </button>
        </div>

        {showAdvanced && (
          <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
            {/* Warning */}
            <div className="p-3 sm:p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
                ⚠️ Important
              </h3>
              <ul className="text-xs sm:text-sm text-yellow-800 dark:text-yellow-300 space-y-1 list-disc list-inside">
                <li>Ensure device is not being used during update</li>
                <li>Do not disconnect power or close browser during update</li>
                <li>Update takes approximately 30-60 seconds</li>
                <li>Device will restart automatically after successful update</li>
                <li>Previous firmware version will be kept as backup</li>
              </ul>
            </div>

            {/* File Selection */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Firmware File (.bin)
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".bin"
                onChange={handleFileSelect}
                disabled={isUploading}
                className="block w-full text-sm text-gray-900 dark:text-white
                         file:mr-4 file:py-2 file:px-4
                         file:rounded-lg file:border-0
                         file:text-sm file:font-semibold
                         file:bg-blue-50 dark:file:bg-blue-900/50
                         file:text-blue-700 dark:file:text-blue-300
                         hover:file:bg-blue-100 dark:hover:file:bg-blue-900/70
                         file:cursor-pointer
                         disabled:opacity-50 disabled:cursor-not-allowed
                         bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg"
              />
            </div>

            {/* Selected File Info */}
            {selectedFile && !selectedFirmware && (
              <div className="p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                  {selectedFile.name}
                </div>
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  Size: {FirmwareService.formatSize(selectedFile.size)}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 sm:gap-3">
              <Button
                variant="primary"
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                className="flex-1"
              >
                {isUploading ? 'Uploading...' : 'Upload Custom Firmware'}
              </Button>
              {isUploading && (
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  className="px-4 sm:px-6"
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}
